/**
 * Serialize graphql-ws `on.error` payloads into something diagnosable.
 *
 * graphql-ws invokes the error listener with `unknown`: an `Error` (connection
 * failure), a WebSocket `CloseEvent` (abnormal close), an `ErrorEvent` / plain
 * `Event` (socket-level error), or an array of GraphQL errors (fatal protocol
 * errors). The old handler did `String(error)`, which rendered every browser
 * event as "[object Event]" — 17 identical, undiagnosable GlitchTip events.
 *
 * This extracts a human-readable message plus a stable `code` that the capture
 * pipeline uses as the fingerprint, so occurrences group by failure mode
 * (e.g. one issue per WS close-code) instead of all collapsing into "unknown".
 */

const READY_STATE_NAMES: Record<number, string> = {
  0: 'CONNECTING',
  1: 'OPEN',
  2: 'CLOSING',
  3: 'CLOSED',
}

export interface SerializedWsError {
  /** Human-readable description used in the captured error message. */
  message: string
  /** Stable grouping code, e.g. 'ws-close-1006', 'ws-error', 'ws-event'. */
  code: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function readyStateSuffix(readyState?: number): string {
  if (typeof readyState !== 'number') return ''
  const name = READY_STATE_NAMES[readyState] ?? String(readyState)
  return `, readyState=${name}`
}

function safeStringify(value: unknown): string {
  try {
    const json = JSON.stringify(value)
    if (typeof json === 'string' && json !== '{}' && json !== 'null') return json
  } catch {
    // Circular structure or throwing getter — fall through.
  }
  return Object.prototype.toString.call(value)
}

/**
 * @param error the raw value graphql-ws passed to `on.error`
 * @param readyState the current `WebSocket.readyState`, when the socket is accessible
 */
export function serializeWsError(error: unknown, readyState?: number): SerializedWsError {
  const rs = readyStateSuffix(readyState)

  // Fatal protocol errors arrive as an array of GraphQL errors.
  if (Array.isArray(error)) {
    const messages = error
      .map((e) => (isRecord(e) && typeof e.message === 'string' ? e.message : safeStringify(e)))
      .join('; ')
    const first: unknown = error[0]
    const extensionsCode =
      isRecord(first) && isRecord(first.extensions) && typeof first.extensions.code === 'string'
        ? first.extensions.code
        : null
    return {
      code: extensionsCode ? `ws-graphql-${extensionsCode}` : 'ws-graphql-errors',
      message: `GraphQL errors over WebSocket: ${messages || '(empty error list)'}${rs}`,
    }
  }

  if (error instanceof Error) {
    return { code: 'ws-error', message: `${error.name}: ${error.message || '(no message)'}${rs}` }
  }

  if (isRecord(error)) {
    // CloseEvent: numeric close code (+ optional reason). Duck-typed — the
    // event may come from another realm, and CloseEvent doesn't exist in SSR.
    if (typeof error.code === 'number') {
      const reason = typeof error.reason === 'string' && error.reason ? `, reason=${error.reason}` : ''
      const clean = typeof error.wasClean === 'boolean' ? `, wasClean=${error.wasClean}` : ''
      return {
        code: `ws-close-${error.code}`,
        message: `WebSocket closed (code=${error.code}${reason}${clean}${rs})`,
      }
    }

    // ErrorEvent: carries a message string.
    if (typeof error.message === 'string' && error.message) {
      return { code: 'ws-error-event', message: `WebSocket error: ${error.message}${rs}` }
    }

    // Plain Event (the old "[object Event]" case): only `type` is available.
    if (typeof error.type === 'string') {
      return { code: 'ws-event', message: `WebSocket "${error.type}" event with no detail${rs}` }
    }

    return { code: 'ws-unknown', message: `Unrecognized WebSocket error: ${safeStringify(error)}${rs}` }
  }

  return { code: 'ws-unknown', message: `Unrecognized WebSocket error: ${String(error)}${rs}` }
}
